import React, { useEffect, useState } from "react";
import { Layout, Rate, Skeleton } from "antd";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { Get } from "../../config/api/get";
import {
  UPLOADS_URL,
  LESSON,
  COACH,
  RATES,
  REVIEWS,
} from "../../config/constants/api";
import dayjs from "dayjs";
import { BiSolidMessageAltDetail } from "react-icons/bi";
import { HiUsers } from "react-icons/hi";
import { RiFileList2Fill } from "react-icons/ri";
import {
  FaArrowRight,
  FaBox,
  FaUserAlt,
} from "react-icons/fa";
import {
  HiOutlineAcademicCap,
  HiOutlineCalendarDays,
  HiOutlineClock,
  HiOutlineCube,
  HiOutlineDocumentText,
  HiOutlineShieldCheck,
} from "react-icons/hi2";
import { BsThreeDots } from "react-icons/bs";
import { MdOutlineFolderShared, MdShowChart } from "react-icons/md";
import { ImageUrl } from "../../helpers/Functions";
import "./dashboard.css";

function studentName(item) {
  const student = item?.student;
  if (!student) return "";
  return `${student.firstName || ""} ${student.lastName || ""}`.trim();
}

function Dashboard() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.userData);
  const token = useSelector((state) => state.user.userToken);
  const [upcomingLessons, setUpcomingLessons] = useState([]);
  const [liveLessons, setLiveLessons] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [rates, setRates] = useState({
    hourlyRate: 0,
    tutoringRate: 0,
    coachingRate: 0,
  });

  const [review, setReview] = useState({
    averageRating: 0,
    totalReviews: 0,
  });

  useEffect(() => {
    if (user) {
      getMyRates();
      getReviewCount();
      getUpcomingLessons();
      getLiveLessons();
      getMyStudents();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getMyRates = () => {
    try {
      Get(RATES.getMyRates, token).then((response) => {
        if (response?.status) {
          setRates(response?.data?.rates);
        } else {
          console.log("response", response);
        }
      });
    } catch (error) {
      console.log("error", error);
    }
  };

  const getReviewCount = () => {
    try {
      Get(REVIEWS.getCoachRatings + user._id, token).then((response) => {
        if (response?.status) {
          setReview(response?.data);
        } else {
          console.log("response", response);
        }
      });
    } catch (error) {
      console.log("error", error);
    }
  };

  const getMyStudents = async () => {
    try {
      const response = await Get(COACH.getMyStudents, token, {
        page: "1",
        limit: "3",
      });
      if (response?.status) {
        setStudents(response?.data?.students);
      } else {
        console.log("error====>", response);
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  const getUpcomingLessons = async (
    pageNumber,
    pageSize,
    search,
    reset = false
  ) => {
    setLoading2(true);
    try {
      const response = await Get(LESSON.getUpcomingLessons, token, {
        page: "1",
        limit: "5",
      });
      setLoading2(false);
      if (response?.status) {
        setUpcomingLessons(response?.data?.docs);
      } else {
        console.log("error====>", response);
      }
    } catch (error) {
      console.log(error.message);
      setLoading2(false);
    }
  };

  const getLiveLessons = async (
    pageNumber,
    pageSize,
    search,
    reset = false
  ) => {
    setLoading(true);
    try {
      const response = await Get(LESSON.getLiveLessons, token, {
        page: "1",
        limit: "5",
      });
      setLoading(false);
      if (response?.status) {
        setLiveLessons(response?.data?.docs);
      } else {
        console.log("error====>", response);
      }
    } catch (error) {
      console.log(error.message);
      setLoading(false);
    }
  };

  const openLesson = (id) => navigate("/lesson-detail/" + id);

  const firstName = user?.firstName || "there";
  const today = dayjs();

  const quickLinks = [
    {
      title: "Upcoming Lessons",
      desc: "See what's next on your calendar",
      path: "/upcoming-lessons",
      icon: <FaUserAlt />,
      tone: "is-pink",
    },
    {
      title: "Live Lessons",
      desc: "Join sessions happening now",
      path: "/live-lessons",
      icon: <FaBox />,
      tone: "is-slate",
    },
    {
      title: "Meetings",
      desc: "Manage meeting requests",
      path: "/meetings",
      icon: <RiFileList2Fill />,
      tone: "is-lavender",
    },
    {
      title: "Completed Lessons",
      desc: "Review finished sessions",
      path: "/completed-lessons",
      icon: <RiFileList2Fill />,
      tone: "is-violet",
    },
    {
      title: "Message",
      desc: "Chat with your students",
      path: "/chat",
      icon: <BiSolidMessageAltDetail />,
      tone: "is-green",
    },
    {
      title: "Students",
      desc: "View and manage students",
      path: "/students",
      icon: <HiUsers />,
      tone: "is-teal",
    },
    {
      title: "Courses",
      desc: "Manage your courses",
      path: "/course-management",
      icon: <HiUsers />,
      tone: "is-sky",
    },
    {
      title: "Lectures",
      desc: "Organize lecture content",
      path: "/lecture-management",
      icon: <HiUsers />,
      tone: "is-mint",
    },
    {
      title: "Community",
      desc: "Engage with discussions",
      path: "/community",
      icon: <HiUsers />,
      tone: "is-amber",
    },
    {
      title: "Quiz",
      desc: "Create and manage quizzes",
      path: "/quiz-management",
      icon: <HiUsers />,
      tone: "is-indigo",
    },
    {
      title: "Resource Library",
      desc: "Share learning assets",
      path: "/asset-management",
      icon: <MdOutlineFolderShared />,
      tone: "is-rose",
    },
  ];

  if (user?.hasTrainingAssociates) {
    quickLinks.push({
      title: "Associate management",
      desc: "Manage training associates",
      path: "/associate-management",
      icon: <HiUsers />,
      tone: "is-teal",
    });
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout className="dash" style={{ minHeight: "100vh", overflow: "hidden" }}>
      <section className="dash-hero">
        <div className="dash-wrap dash-hero-inner">
          <div>
            <h1 className="dash-hello">Welcome back, {firstName}! 👋</h1>
            <p className="dash-hello-sub">
              Here's what's happening with your teaching schedule today.
            </p>
          </div>
          <div className="dash-date-card">
            <div className="dash-date-copy">
              <span className="dash-date-label">Today's Date</span>
              <span className="dash-date-value">{today.format("MMMM D, YYYY")}</span>
            </div>
            <span className="dash-date-icon" aria-hidden>
              <HiOutlineCalendarDays />
            </span>
          </div>
        </div>
      </section>

      <div className="dash-wrap">
        <div className="dash-stats">
          <article className="dash-stat">
            <div className="dash-stat-icon is-green">
              <HiOutlineCube />
            </div>
            <div className="dash-stat-copy">
              <p className="dash-stat-label">Upcoming Lessons</p>
              <p className="dash-stat-value">{upcomingLessons?.length || 0}</p>
              <p className="dash-stat-meta is-up">
                Next on your calendar <MdShowChart />
              </p>
            </div>
          </article>
          <article className="dash-stat">
            <div className="dash-stat-icon is-purple">
              <HiOutlineAcademicCap />
            </div>
            <div className="dash-stat-copy">
              <p className="dash-stat-label">Live Lessons</p>
              <p className="dash-stat-value">{liveLessons?.length || 0}</p>
              <p className="dash-stat-meta is-muted">
                Happening now <HiOutlineClock />
              </p>
            </div>
          </article>
          <article className="dash-stat">
            <div className="dash-stat-icon is-orange">
              <HiOutlineDocumentText />
            </div>
            <div className="dash-stat-copy">
              <p className="dash-stat-label">Average Rating</p>
              <p className="dash-stat-value">{review.averageRating || 0}</p>
              <p className="dash-stat-meta is-orange">
                From students <HiOutlineClock />
              </p>
            </div>
          </article>
          <article className="dash-stat">
            <div className="dash-stat-icon is-blue">
              <HiOutlineShieldCheck />
            </div>
            <div className="dash-stat-copy">
              <p className="dash-stat-label">Total Reviews</p>
              <p className="dash-stat-value">{review.totalReviews || 0}</p>
              <p className="dash-stat-meta is-up">
                Student feedback <MdShowChart />
              </p>
            </div>
          </article>
        </div>

        <section className="dash-panel">
          <div className="dash-panel-head">
            <h2 className="dash-panel-title">Your Profile</h2>
            <p className="dash-panel-sub">
              Ratings, reviews, and hourly rate at a glance.
            </p>
          </div>
          <div className="dash-profile">
            <img
              className="dash-profile-avatar"
              alt=""
              src={
                !user?.image
                  ? ImageUrl("avatar.png")
                  : UPLOADS_URL + "/" + user.image
              }
            />
            <div className="dash-profile-copy">
              <h3>
                {user?.firstName} {user?.lastName}
              </h3>
              <p>
                {user?.bio?.slice(0, 150)}
                {user?.bio?.length > 150 ? "..." : ""}
              </p>
              <div className="dash-profile-meta">
                <Rate
                  disabled
                  allowHalf
                  value={review.averageRating}
                  style={{ color: "#FABF35", fontSize: 16 }}
                />
                <span className="dash-profile-reviews">
                  {review.totalReviews} Reviews
                </span>
                {rates.hourlyRate ? (
                  <span className="dash-profile-rate-pill">
                    Hourly Rates : ${rates.hourlyRate}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </section>

        <section className="dash-panel">
          <div className="dash-panel-head">
            <h2 className="dash-panel-title">Quick Access</h2>
            <p className="dash-panel-sub">
              Jump into your teaching tools and features.
            </p>
          </div>
          <div className="dash-quick-grid">
            {quickLinks.map((item) => (
              <button
                key={item.path}
                type="button"
                className={`dash-quick ${item.tone}`}
                onClick={() => navigate(item.path)}
              >
                <span className="dash-quick-icon">{item.icon}</span>
                <span className="dash-quick-copy">
                  <span className="dash-quick-title">{item.title}</span>
                  <span className="dash-quick-desc">{item.desc}</span>
                </span>
                <FaArrowRight className="dash-quick-arrow" />
              </button>
            ))}
          </div>
        </section>

        <div className="dash-cols">
          <section className="dash-panel">
            <div className="dash-panel-head is-row">
              <h2 className="dash-panel-title">Live Lessons</h2>
              <button
                type="button"
                className="dash-view-all"
                onClick={() => navigate("/live-lessons")}
              >
                View All
              </button>
            </div>
            {loading ? (
              <Skeleton active />
            ) : liveLessons?.length ? (
              liveLessons.map((item) => (
                <button
                  type="button"
                  key={item._id}
                  className="dash-lesson"
                  onClick={() => openLesson(item._id)}
                >
                  <div className="dash-lesson-date">
                    <span>{dayjs(item.lessonDate).format("MMM")}</span>
                    <strong>{dayjs(item.lessonDate).format("DD")}</strong>
                  </div>
                  <div className="dash-lesson-body">
                    <p className="dash-lesson-id">
                      Lesson ID: {item.lessonId || "—"}
                    </p>
                    <p className="dash-lesson-title">{studentName(item)}</p>
                    <p className="dash-lesson-meta">
                      {item.lessonType || "Lesson"}
                      {item.charges != null ? ` · $${item.charges}` : ""}
                    </p>
                  </div>
                  <span className="dash-pill">Live</span>
                </button>
              ))
            ) : (
              <div className="dash-empty">
                <div className="dash-empty-icon">
                  <HiOutlineCalendarDays />
                </div>
                <h4>No Live Lessons</h4>
                <p>You don't have any live lessons scheduled at the moment.</p>
              </div>
            )}
          </section>

          <section className="dash-panel">
            <div className="dash-panel-head is-row">
              <h2 className="dash-panel-title">Upcoming Lessons</h2>
              <button
                type="button"
                className="dash-view-all"
                onClick={() => navigate("/upcoming-lessons")}
              >
                View All
              </button>
            </div>
            {loading2 ? (
              <Skeleton active />
            ) : upcomingLessons?.length ? (
              upcomingLessons.map((item) => (
                <div
                  key={item._id}
                  className="dash-lesson"
                  role="button"
                  tabIndex={0}
                  onClick={() => openLesson(item._id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") openLesson(item._id);
                  }}
                >
                  <div className="dash-lesson-date">
                    <span>{dayjs(item.lessonDate).format("MMM")}</span>
                    <strong>{dayjs(item.lessonDate).format("DD")}</strong>
                  </div>
                  <div className="dash-lesson-body">
                    <p className="dash-lesson-id">
                      Lesson ID: {item.lessonId || "—"}
                    </p>
                    <p className="dash-lesson-title">{studentName(item)}</p>
                    <p className="dash-lesson-meta">
                      {item.lessonType || "Lesson"}
                      {item.charges != null ? ` · $${item.charges}` : ""}
                      {item.status ? ` · ${item.status}` : ""}
                    </p>
                  </div>
                  <div className="dash-lesson-side">
                    <span
                      className={`dash-pill ${
                        item.isPaid ? "is-paid" : "is-pending"
                      }`}
                    >
                      {item.isPaid ? "Paid" : "Pending"}
                    </span>
                    <span className="dash-more" aria-hidden>
                      <BsThreeDots />
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="dash-empty">
                <div className="dash-empty-icon">
                  <HiOutlineCalendarDays />
                </div>
                <h4>No Upcoming Lessons</h4>
                <p>
                  You don't have any upcoming lessons scheduled at the moment.
                </p>
              </div>
            )}
          </section>

          <section className="dash-panel">
            <div className="dash-panel-head is-row">
              <h2 className="dash-panel-title">My Students</h2>
              {students.length > 4 ? (
                <button
                  type="button"
                  className="dash-view-all"
                  onClick={() => navigate("/students")}
                >
                  View All
                </button>
              ) : null}
            </div>
            {loading2 ? (
              <Skeleton active />
            ) : students?.length ? (
              students.map((item, index) => (
                <div key={item._id || index} className="dash-activity">
                  <span
                    className={`dash-activity-icon ${
                      index % 3 === 0
                        ? "is-orange"
                        : index % 3 === 1
                        ? "is-purple"
                        : "is-green"
                    }`}
                  >
                    {item.image ? (
                      <img
                        alt=""
                        src={UPLOADS_URL + "/" + item.image}
                      />
                    ) : (
                      <FaUserAlt />
                    )}
                  </span>
                  <div className="dash-activity-copy">
                    <h4>
                      {item.firstName} {item.lastName}
                    </h4>
                    <p>Student connected with your training profile.</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="dash-empty">
                <div className="dash-empty-icon">
                  <FaUserAlt />
                </div>
                <h4>No Students Found</h4>
                <p>You don't have any students assigned yet.</p>
              </div>
            )}
          </section>
        </div>
      </div>
    </Layout>
  );
}

export default Dashboard;
